import {
  Box,
  Button,
  FormControl,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { usePartneringFamilies } from '../Model/V1CasesModel';
import React, { useState } from 'react';
import {
  Add as AddIcon,
  FilterList as FilterListIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import { ArrangementPhase, Permission } from '../GeneratedClient';
import { CreatePartneringFamilyDrawer } from './CreatePartneringFamilyDrawer';
import { useScrollMemory } from '../Hooks/useScrollMemory';
import { useLocalStorage } from '../Hooks/useLocalStorage';
import { usePolicy } from '../Model/PolicyModel';
import { SearchBar } from '../Shell/SearchBar';
import { filterFamiliesByText } from '../Families/FamilyUtils';
import { usePersonAndFamilyLookup } from '../Model/DirectoryModel';
import {
  useAllPartneringFamiliesPermissions,
  useGlobalPermissions,
} from '../Model/SessionModel';
import { useScreenTitle } from '../Shell/ShellScreenTitle';
import { ProgressBackdrop } from '../Shell/ProgressBackdrop';
import { useAppNavigate } from '../Hooks/useAppNavigate';
import { useCustomFieldFilters } from '../Generic/CustomFieldsFilter/useCustomFieldFilters';
import { matchesCustomFieldFilters } from '../Generic/CustomFieldsFilter/matchesCustomFieldFilters';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { forceCheck } from '../Utilities/reactLazyLoadInterop';
import { PartneringFamilyTableItem } from './PartneringFamilies/PartneringFamilyTableItem';
import { arrangementStatusSummary } from './PartneringFamilies/arrangementStatusSummary';
import {
  ArrangementsFilter,
  normalizeArrangementsFilter,
} from './PartneringFamilies/types';
import { containedStickyHeaderTableSx } from '../Utilities/stickyHeaderTableSx';
import { WideTableContainer } from '../Utilities/WideTableContainer';
import { wideTablePageSx } from '../Utilities/wideTablePageSx';
import { getFamilyCounty } from '../Utilities/getFamilyCounty';
import { CountyFilter } from '../Generic/CountyFilter';
import { useVisibleReferrals } from '../Model/Data';
import {
  normalizePartneringFamiliesSortMode,
  openReferralByFamilyId,
  PartneringFamiliesSortMode,
  sortPartneringFamilies,
} from './PartneringFamilies/sortPartneringFamilies';
import { useSidePanel } from '../Hooks/useSidePanel';
import { PartneringFamilyCustomFieldFiltersSidePanel } from './PartneringFamilies/PartneringFamilyCustomFieldFiltersSidePanel';
import {
  FUNCTION_ASSIGNMENTS_FEATURE_FLAG,
  REFERRALS_FEATURE_FLAG,
  UPDATE_TEST_FAMILY_FEATURE_FLAG,
} from '../featureFlags';
import {
  AssignmentFilterSelectionsByRole,
  assignmentRolesForColumns,
  matchesAssignmentFilters,
} from '../FunctionAssignments/assignmentRoleColumns';
import { AssignmentRoleFilters } from '../FunctionAssignments/AssignmentRoleFilters';

const PARTNERING_FAMILIES_SORT_STORAGE_KEY = 'partnering-families-sortMode';
const ARRANGEMENTS_FILTER_STORAGE_KEY =
  'partnering-families-arrangementsFilter';

function isSetupOrActiveArrangementPhase(phase: ArrangementPhase | undefined) {
  return (
    phase === ArrangementPhase.Started ||
    phase === ArrangementPhase.SettingUp ||
    phase === ArrangementPhase.ReadyToStart
  );
}

function PartneringFamilies() {
  const appNavigate = useAppNavigate();
  const personAndFamilyLookup = usePersonAndFamilyLookup();
  const globalPermissions = useGlobalPermissions();
  const {
    SidePanel: CustomFieldFiltersSidePanel,
    openSidePanel: openCustomFieldFiltersSidePanel,
    closeSidePanel: closeCustomFieldFiltersSidePanel,
  } = useSidePanel();

  const partneringFamilies = usePartneringFamilies();
  const visibleReferralRecords = useVisibleReferrals();
  const visibleReferrals = React.useMemo(
    () => visibleReferralRecords.map((referralInfo) => referralInfo.referral),
    [visibleReferralRecords]
  );
  const openReferralByFamily = React.useMemo(
    () => openReferralByFamilyId(visibleReferrals),
    [visibleReferrals]
  );

  const policy = usePolicy();
  const arrangementTypes = policy.referralPolicy?.arrangementPolicies?.map(
    (a) => {
      return a.arrangementType!;
    }
  );

  const referralCustomFields = React.useMemo(() => {
    return policy.referralPolicy?.customFields || [];
  }, [policy.referralPolicy?.customFields]);
  const customFieldCount = referralCustomFields.length;

  const [filterText, setFilterText] = useState('');
  const [countyFilter, setCountyFilter] = useState<(string | null)[]>([]);
  const [storedSortMode, setStoredSortMode] =
    useLocalStorage<PartneringFamiliesSortMode>(
      PARTNERING_FAMILIES_SORT_STORAGE_KEY,
      'lastNameAsc'
    );
  const sortMode = normalizePartneringFamiliesSortMode(storedSortMode);

  function setSortMode(value: PartneringFamiliesSortMode) {
    setStoredSortMode(value);
  }

  const filteredPartneringFamilies = React.useMemo(
    () => filterFamiliesByText(partneringFamilies, filterText),
    [filterText, partneringFamilies]
  );

  const {
    selectedValuesByField: selectedCustomFieldValuesByField,
    setSelectedValuesForField: setSelectedCustomFieldValuesForField,
    getOptionsForField: getCustomFieldFilterOptionsForField,
  } = useCustomFieldFilters({
    customFields: referralCustomFields,
    items: partneringFamilies,
    isBlank: (family, fieldName) =>
      family.partneringFamilyInfo?.openV1Case?.missingCustomFields?.includes(
        fieldName
      ) ?? false,
    getValue: (family, fieldName) =>
      family.partneringFamilyInfo?.openV1Case?.completedCustomFields?.find(
        (f) => f.customFieldName === fieldName
      )?.value,
  });
  const activeCustomFieldFilterCount = Object.values(
    selectedCustomFieldValuesByField
  ).filter((selectedValues) => selectedValues.length > 0).length;
  const [assignmentFilters, setAssignmentFilters] =
    useState<AssignmentFilterSelectionsByRole>({});

  const permissions = useAllPartneringFamiliesPermissions();
  const functionAssignmentsEnabled = useFeatureFlagEnabled(
    FUNCTION_ASSIGNMENTS_FEATURE_FLAG
  );
  const canViewFunctionAssignments =
    functionAssignmentsEnabled === true &&
    permissions(Permission.ViewV1CaseFunctionAssignments);
  const assignmentRoles = canViewFunctionAssignments
    ? assignmentRolesForColumns(
        policy.referralPolicy?.functionAssignmentPolicies?.map(
          (assignmentPolicy) => assignmentPolicy.assignmentRole
        ) ?? [],
        partneringFamilies.flatMap(
          (family) =>
            family.partneringFamilyInfo?.openV1Case
              ?.assignedIndividualVolunteers ?? []
        )
      )
    : [];

  useScrollMemory();

  function openFamily(familyId: string) {
    appNavigate.family(familyId);
  }

  const [
    createPartneringFamilyDialogOpen,
    setCreatePartneringFamilyDialogOpen,
  ] = useState(false);
  const [expandedView, setExpandedView] = useLocalStorage(
    'partnering-families-expanded',
    true
  );

  const handleExpandCollapse = (
    _event: React.MouseEvent<HTMLElement>,
    newExpandedView: boolean | null
  ) => {
    if (newExpandedView !== null) {
      setExpandedView(newExpandedView);
    }
  };

  const [storedArrangementsFilter, setStoredArrangementsFilter] =
    useLocalStorage<ArrangementsFilter | null>(
      ARRANGEMENTS_FILTER_STORAGE_KEY,
      'All'
    );
  const arrangementsFilter = normalizeArrangementsFilter(
    storedArrangementsFilter
  );

  function setArrangementsFilter(value: ArrangementsFilter | null) {
    if (value === null) {
      return;
    }

    setStoredArrangementsFilter(value);
  }

  const sortedPartneringFamilies = React.useMemo(
    () =>
      sortPartneringFamilies(
        filteredPartneringFamilies
          .filter((family) =>
            matchesCustomFieldFilters({
              item: family,
              customFields: referralCustomFields,
              selectedValuesByField: selectedCustomFieldValuesByField,
              isBlank: (f, fieldName) =>
                f.partneringFamilyInfo?.openV1Case?.missingCustomFields?.includes(
                  fieldName
                ) ?? false,
              getValue: (f, fieldName) =>
                f.partneringFamilyInfo?.openV1Case?.completedCustomFields?.find(
                  (x) => x.customFieldName === fieldName
                )?.value,
            })
          )
          .filter((family) => {
            if (countyFilter.length === 0) return true;

            const county = getFamilyCounty(family);
            return county === null
              ? countyFilter.includes(null)
              : countyFilter.includes(county);
          })
          .filter((family) => {
            if (!canViewFunctionAssignments) return true;

            return matchesAssignmentFilters(
              family.partneringFamilyInfo?.openV1Case
                ?.assignedIndividualVolunteers ?? [],
              assignmentFilters
            );
          })
          .filter((family) => {
            const familyId = family.family?.id;
            const openCase = family.partneringFamilyInfo?.openV1Case;
            const arrangements = openCase?.arrangements ?? [];
            const hasOpenReferralWithoutCase =
              !openCase && !!familyId && openReferralByFamily.has(familyId);

            switch (arrangementsFilter) {
              case 'All':
                return true;

              case 'Intake':
                if (hasOpenReferralWithoutCase) return true;
                if (!openCase) return false;

                return arrangements.length === 0;

              case 'Active':
                return arrangements.some(
                  (arrangement) =>
                    arrangement.phase === ArrangementPhase.Started
                );

              case 'Setup':
                return arrangements.some(
                  (arrangement) =>
                    arrangement.phase === ArrangementPhase.SettingUp ||
                    arrangement.phase === ArrangementPhase.ReadyToStart
                );

              case 'Active + Setup':
                return arrangements.some((arrangement) =>
                  isSetupOrActiveArrangementPhase(arrangement.phase)
                );

              default:
                return true;
            }
          }),
        sortMode,
        openReferralByFamily
      ),
    [
      assignmentFilters,
      arrangementsFilter,
      canViewFunctionAssignments,
      countyFilter,
      filteredPartneringFamilies,
      openReferralByFamily,
      referralCustomFields,
      selectedCustomFieldValuesByField,
      sortMode,
    ]
  );

  React.useEffect(() => {
    forceCheck();
  }, [
    assignmentFilters,
    arrangementsFilter,
    filterText,
    selectedCustomFieldValuesByField,
    sortMode,
    openReferralByFamily,
  ]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const updateTestFamilyFlagEnabled = useFeatureFlagEnabled(
    UPDATE_TEST_FAMILY_FEATURE_FLAG
  );

  const canCreateFamily =
    permissions(Permission.EditFamilyInfo) &&
    permissions(Permission.CreateV1Case);
  const tableColumnCount =
    3 +
    assignmentRoles.length +
    referralCustomFields.length +
    (expandedView ? 0 : (arrangementTypes?.length ?? 0));
  const tableMinWidth = Math.max(700, tableColumnCount * 160);
  const hasFeaturebaseChat = globalPermissions(Permission.AccessSupportScreen);

  const referralsEnabled = useFeatureFlagEnabled(REFERRALS_FEATURE_FLAG);
  const showAddFamilyButton = !referralsEnabled && canCreateFamily;

  useScreenTitle('Clients');

  return !arrangementTypes ? (
    <ProgressBackdrop>
      <p>Loading families...</p>
    </ProgressBackdrop>
  ) : (
    <Box sx={wideTablePageSx(hasFeaturebaseChat)}>
      <Box sx={{ flex: '0 0 auto' }}>
        <Stack
          direction="row"
          sx={{
            marginTop: 2,
            marginBottom: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {canCreateFamily && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreatePartneringFamilyDialogOpen(true)}
              sx={{
                marginRight: 'auto',
                visibility: showAddFamilyButton ? 'visible' : 'hidden',
                pointerEvents: showAddFamilyButton ? 'auto' : 'none',
              }}
            >
              Add new client family
            </Button>
          )}
          <ToggleButtonGroup
            sx={{ marginLeft: 'auto' }}
            value={arrangementsFilter}
            exclusive
            onChange={(_, value) => setArrangementsFilter(value)}
            size={isMobile ? 'medium' : 'small'}
            aria-label="row expansion"
          >
            <Tooltip title="Shows all cases" arrow>
              <ToggleButton value={'All'} aria-label="expanded">
                All
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Shows open cases with no arrangements yet" arrow>
              <ToggleButton value={'Intake'}>Intake</ToggleButton>
            </Tooltip>
            <Tooltip
              title="Shows cases with at least one active arrangement"
              arrow
            >
              <ToggleButton value={'Active'} aria-label="collapsed">
                Active
              </ToggleButton>
            </Tooltip>
            <Tooltip
              title="Shows cases with arrangements in the setup phase"
              arrow
            >
              <ToggleButton value={'Setup'} aria-label="collapsed">
                Setup
              </ToggleButton>
            </Tooltip>
            <Tooltip
              title="Shows cases with arrangements that are either active or in setup"
              arrow
            >
              <ToggleButton value={'Active + Setup'} aria-label="collapsed">
                Active + Setup
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          <CountyFilter
            families={partneringFamilies}
            value={countyFilter}
            onChange={setCountyFilter}
          />
          {canViewFunctionAssignments && (
            <AssignmentRoleFilters
              assignmentRoles={assignmentRoles}
              assignments={partneringFamilies.flatMap(
                (family) =>
                  family.partneringFamilyInfo?.openV1Case
                    ?.assignedIndividualVolunteers ?? []
              )}
              selectedValuesByRole={assignmentFilters}
              onChange={(assignmentRole, selectedValues) =>
                setAssignmentFilters((current) => ({
                  ...current,
                  [assignmentRole]: selectedValues,
                }))
              }
              personLookup={(personId) =>
                personAndFamilyLookup(personId).person
              }
            />
          )}
          {customFieldCount > 0 && (
            <FormControl
              sx={{
                position: 'relative',
                minWidth: { xs: '100%', sm: 0 },
                maxWidth: { xs: '100%', sm: '16rem' },
              }}
            >
              <Select
                labelId="partneringFamilyCustomFieldsFilter"
                displayEmpty
                value=""
                open={false}
                variant="standard"
                onClick={() => openCustomFieldFiltersSidePanel()}
                sx={{
                  minWidth: { xs: '100%', sm: 0 },
                  maxWidth: '100%',
                  '& .MuiSelect-iconOpen': { transform: 'none' },
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  },
                }}
                input={<InputBase />}
                IconComponent={FilterListIcon}
                SelectDisplayProps={{
                  title: `Custom fields (${activeCustomFieldFilterCount}/${customFieldCount})`,
                }}
                renderValue={() =>
                  `Custom fields (${activeCustomFieldFilterCount}/${customFieldCount})`
                }
              >
                <MenuItem value="" sx={{ display: 'none' }} />
              </Select>
            </FormControl>
          )}

          <SearchBar value={filterText} onChange={setFilterText} />
          <ToggleButtonGroup
            value={expandedView}
            exclusive
            onChange={handleExpandCollapse}
            size={isMobile ? 'medium' : 'small'}
            aria-label="row expansion"
          >
            <ToggleButton value={true} aria-label="expanded">
              <UnfoldMoreIcon />
            </ToggleButton>
            <ToggleButton value={false} aria-label="collapsed">
              <UnfoldLessIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" sx={{ my: 2, justifyContent: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="partnering-families-sort-label">Sort by</InputLabel>
            <Select
              labelId="partnering-families-sort-label"
              value={sortMode}
              label="Sort by"
              onChange={(event: SelectChangeEvent) =>
                setSortMode(event.target.value as PartneringFamiliesSortMode)
              }
            >
              <MenuItem value="lastNameAsc">Last name (ascending)</MenuItem>
              <MenuItem value="lastNameDesc">Last name (descending)</MenuItem>
              <MenuItem value="firstNameAsc">First name (ascending)</MenuItem>
              <MenuItem value="firstNameDesc">First name (descending)</MenuItem>
              <MenuItem value="dateOpenedDesc">
                Date opened (descending)
              </MenuItem>
              <MenuItem value="dateOpenedAsc">Date opened (ascending)</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <CustomFieldFiltersSidePanel>
          <PartneringFamilyCustomFieldFiltersSidePanel
            customFields={referralCustomFields}
            getOptionsForField={getCustomFieldFilterOptionsForField}
            selectedValuesByField={selectedCustomFieldValuesByField}
            onFieldChange={setSelectedCustomFieldValuesForField}
            onClose={closeCustomFieldFiltersSidePanel}
          />
        </CustomFieldFiltersSidePanel>
      </Box>
      <Box
        className="cases-table"
        sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}
      >
        <WideTableContainer>
          <Table
            stickyHeader
            sx={{
              ...containedStickyHeaderTableSx,
              minWidth: tableMinWidth,
            }}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell>Client Family</TableCell>
                <TableCell>Case Status</TableCell>
                <TableCell>County</TableCell>
                {canViewFunctionAssignments &&
                  assignmentRoles.map((assignmentRole) => (
                    <TableCell key={assignmentRole}>{assignmentRole}</TableCell>
                  ))}
                {referralCustomFields.map((field) => (
                  <TableCell
                    key={field.name}
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    {field.name}
                  </TableCell>
                ))}

                {!expandedView &&
                  arrangementTypes?.map((arrangementType) => (
                    <TableCell
                      key={arrangementType}
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      {arrangementType}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedPartneringFamilies.map((partneringFamily) => (
                <PartneringFamilyTableItem
                  key={partneringFamily.family?.id}
                  partneringFamily={partneringFamily}
                  arrangementTypes={arrangementTypes}
                  arrangementsFilter={arrangementsFilter}
                  expandedView={expandedView}
                  openArrangement={(familyId, v1CaseId, arrangementId) =>
                    appNavigate.family(familyId, v1CaseId, arrangementId)
                  }
                  openFamily={openFamily}
                  assignmentRoles={
                    canViewFunctionAssignments ? assignmentRoles : []
                  }
                  assignmentPersonLookup={(personId) =>
                    personAndFamilyLookup(personId).person
                  }
                  referralCustomFields={referralCustomFields}
                  arrangementStatusSummary={arrangementStatusSummary}
                  updateTestFamilyFlagEnabled={updateTestFamilyFlagEnabled}
                />
              ))}
            </TableBody>
          </Table>
        </WideTableContainer>

        {createPartneringFamilyDialogOpen && (
          <CreatePartneringFamilyDrawer
            onClose={(partneringFamilyId) => {
              setCreatePartneringFamilyDialogOpen(false);

              if (!partneringFamilyId) return;

              openFamily(partneringFamilyId);
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export { PartneringFamilies };
